'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

// Configurable idle timeout in milliseconds
// Example: 5 minutes = 300000, 30 seconds for testing = 30000
const IDLE_TIMEOUT = 300000; // Change this as needed

interface IdleTimeoutModalProps {
  onRefresh?: () => any;
}
const IdleTimeoutModal = ( { onRefresh } : IdleTimeoutModalProps) => {

  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Start or reset the idle timer
  const startTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, IDLE_TIMEOUT);
  }, []);

  // Handle any user activity to reset timer (only when modal is NOT open)
  const handleActivity = useCallback(() => {
    if (!isOpen) {
      startTimer();
    }
    // Do nothing if modal is open — user must explicitly close it
  }, [isOpen, startTimer]);

  const triggerRefresh = () => {
    if (typeof onRefresh === "function") {
      onRefresh();
    } else {
      window.location.reload();
    }
  };

  // Close modal and refresh page
  const handleCloseAndRefresh = () => {
    setIsOpen(false);
    // Small delay to allow modal close animation (optional, feels smoother)
   setTimeout(triggerRefresh, 300);
  };

  useEffect(() => {
    // Start timer on mount
    startTimer();

    const events: (keyof DocumentEventMap)[] = [
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [startTimer, handleActivity]);

  return (
    <Modal
      isOpen={isOpen}
      centered
      backdrop="static"   // Prevents closing on backdrop click
      keyboard={false}    // Prevents closing with Escape key
      toggle={handleCloseAndRefresh} // X button triggers refresh
    >
      <ModalHeader toggle={handleCloseAndRefresh} className="pb-2">
        <h5 className="modal-title fw-bold">See best deals available now</h5>
      </ModalHeader>

      <ModalBody className="text-cente">
        <p className="text-muted mb-0">
          Hotels change their prices frequently. It's been a while, so we'll
          need to refresh the page to get the latest prices and availability
          for your stay.
        </p>
      </ModalBody>

      <ModalFooter>
        <button
            type='button'
          className="button px-20 fw-400 text-14 -outline-blue-1 h-41 text-blue-1 onHover d-flex align-items-center gap-1 mx-auto fw-400"
          onClick={handleCloseAndRefresh}
        >
          Refresh
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default IdleTimeoutModal;